import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../../Contexts/AuthContext";
import { reportService } from "../../../Api/Services/reportService";
import { customerService } from "../../../Api/Services/customerService";
import { SimplifiedCustomer } from "../../../Models/Customer";
import { CustomerType } from "../../../Enums/CustomerType";
import { ReportFormat } from "../../../Enums/ReportFormat";
import { formatCnpj, formatCpf } from "../../../Utils/DataFormatters";
import { FaEye, FaFileCsv, FaUserSlash } from "react-icons/fa";
import { ButtonSpinner } from "../../../Components/Button/Button";
import { toast } from "react-toastify";
import SmartNavbar from "../../../Components/SmartNavbar/SmartNavbar";
import PageTitle from "../../../Components/PageTitle/PageTitle";
import SearchBar from "../../../Components/SearchBar/SearchBar";
import ReportFormatModal from "../../../Components/ReportFormatModal/ReportFormatModal";
import Pagination from "../../../Components/Pagination/Pagination";
import PaginationSummary from "../../../Components/Pagination/PaginationSummary";

const AdminCustomerPage = () => {
  const { isAdmin, isOperator } = useAuth();
  const navigate = useNavigate();

  const [customers, setCustomers] = useState<SimplifiedCustomer[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const customersPerPage = 16;
  const location = useLocation();
  // ?busca= pre-fills the search, e.g. when opening a notification
  const [search, setSearch] = useState(() => new URLSearchParams(location.search).get("busca") ?? "");

  useEffect(() => {
    const busca = new URLSearchParams(location.search).get("busca");
    if (busca !== null) {
      setSearch(busca);
    }
  }, [location.search]);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [isReportFormatModalOpen, setIsReportFormatModalOpen] = useState(false);

  const basePath = isAdmin ? "/admin" : "/operador";

  useEffect(() => {
    loadCustomers(currentPage, search);
  }, [currentPage, search]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  const loadCustomers = async (page: number = 1, searchTerm: string = "") => {
    try {
      setIsLoading(true);
      const data = await customerService.getAll({
        page,
        pageSize: customersPerPage,
        search: searchTerm
      });

      setCustomers(data.items);
      setTotalPages(data.totalPages);
      setTotalItems(data.totalItems);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setCurrentPage(1);
    setSearch(value);
  };

  const handleGenerateReport = async (format: ReportFormat) => {
    try {
      setIsGeneratingReport(true);
      const report = await reportService.createCustomerReport({ search: search || null, format });
      toast.success("Relatório gerado. Abrindo download...");
      window.open(report.url, "_blank", "noopener");
    } catch (error) {
      toast.error("Não foi possível gerar o relatório.");
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const handleOpenReportModal = () => {
    setIsReportFormatModalOpen(true);
  };

  const handleSelectReportFormat = (format: ReportFormat) => {
    setIsReportFormatModalOpen(false);
    handleGenerateReport(format);
  };

  const showSearchBar = search.length > 0 || customers.length > 0;

  const renderPagination = () => {
    if (isLoading || customers.length === 0) {
      return null;
    }

    return (
      <>
        <PaginationSummary
          totalItems={totalItems}
          singularLabel={"cliente encontrado"}
          pluralLabel={"clientes encontrados"}
        />

        {totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        )}
      </>
    );
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SmartNavbar />
      <div className="p-4 sm:p-14 max-w-full flex-1 flex flex-col">
        <PageTitle className="my-6 sm:my-0 sm:mb-8">{"CLIENTES"}</PageTitle>

        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4">
          {showSearchBar && (
            <div className="w-full sm:flex-1 sm:max-w-2xl">
              <h3 className="text-sm font-semibold mb-2">PESQUISA</h3>
              <SearchBar
                onSearch={handleSearch}
                value={search}
                className="w-full py-3"
                placeholder="Buscar por nome ou documento do cliente..."
                borderClass="border-gray-300"
                iconClass="text-primary-600"
              />
            </div>
          )}
          {(isAdmin || isOperator) && (
            <div className={`flex gap-2 ${totalItems > 0 ? "flex-row sm:flex-col" : "flex-col"}`}>
              {isAdmin && (
                <button
                  className={`uppercase sm:normal-case ${totalItems > 0 ? "flex-1 sm:flex-none" : "w-full sm:w-auto"} flex items-center justify-center sm:justify-start gap-1.5 sm:gap-1 py-2 sm:py-0 rounded sm:rounded-none bg-primary-600 sm:bg-transparent text-white sm:text-primary-600 text-sm sm:text-base font-semibold sm:font-normal hover:bg-primary-700 sm:hover:bg-transparent sm:hover:text-primary-700`}
                  onClick={() => navigate("/admin/clientes/importar")}
                >
                  <FaFileCsv /> Importar clientes
                </button>
              )}

              {totalItems > 0 && (
                <button
                  className="uppercase sm:normal-case flex-1 sm:flex-none flex items-center justify-center sm:justify-start gap-1.5 sm:gap-1 py-2 sm:py-0 rounded sm:rounded-none bg-primary-600 sm:bg-transparent text-white sm:text-primary-600 text-sm sm:text-base font-semibold sm:font-normal hover:bg-primary-700 sm:hover:bg-transparent sm:hover:text-primary-700 disabled:opacity-60"
                  onClick={handleOpenReportModal}
                  disabled={isGeneratingReport}
                >
                  {isGeneratingReport ? <ButtonSpinner /> : <FaFileCsv />} {"Exportar relatório"}
                </button>
              )}
            </div>
          )}
        </div>
        <ReportFormatModal
          isOpen={isReportFormatModalOpen}
          onClose={() => setIsReportFormatModalOpen(false)}
          onSelect={handleSelectReportFormat}
        />

        {/* MOBILE CARDS */}
        <div className="sm:hidden flex flex-col gap-4 mt-4">
          {isLoading
            ? Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={`mobile-skel-${i}`}
                  className="animate-pulse bg-white border rounded p-4 shadow-sm space-y-2"
                >
                  <div className="h-4 bg-gray-200 rounded w-24" />
                  <div className="h-3 bg-gray-200 rounded w-3/4" />
                  <div className="h-3 bg-gray-200 rounded w-2/3" />
                </div>
              ))
            : customers.length === 0
              ? (
                <div className="flex-1 flex flex-col items-center justify-center gap-3 text-gray-500">
                  <FaUserSlash className="text-3xl" />
                  <p className="text-center text-sm sm:text-base">
                    {"Nenhum cliente encontrado"}
                  </p>
                </div>
              )
              : customers.map((c, index) => {
                  const isIP = c.customerType === CustomerType.IndividualPerson;
                  return (
                    <div
                      key={c.id}
                      className="border rounded p-4 shadow-sm bg-white"
                    >
                      <h2 className="font-semibold mb-2">{"Cliente"} {index + 1}</h2>
                      <p><span className="font-medium">{"Tipo"}:</span> {isIP ? "Pessoa Física" : "Pessoa Jurídica"}</p>
                      <p><span className="font-medium">{"Nome"}:</span> {isIP ? c.individualPerson?.fullName : c.legalEntity?.companyName}</p>
                      <p><span className="font-medium">CPF / CNPJ:</span> {isIP ? formatCpf(c.individualPerson?.cpf ?? "") : formatCnpj(c.legalEntity?.cnpj ?? "")}</p>

                      <Link
                        to={`${basePath}/clientes/${c.id}`}
                        className="text-primary-600 hover:underline flex items-center gap-1 mt-2"
                      >
                        <FaEye /> {"Ver detalhes"}
                      </Link>
                    </div>
                  );
                })}
        </div>

        {/* DESKTOP TABLE */}
        {customers.length === 0 && !isLoading ? (
          <div className="hidden sm:flex flex-col items-center justify-center gap-3 text-gray-500 flex-1">
            <FaUserSlash className="text-3xl" />
            <p className="text-center text-sm sm:text-base">
              {"Nenhum cliente encontrado"}
            </p>
          </div>
        ) : (
          <table className="hidden sm:table w-full border border-gray-300 border-collapse table-fixed mt-4">
            <thead className="bg-primary-120">
              <tr>
                <th className="border p-3 text-left w-32">{"Tipo"}</th>
                <th className="border p-3 text-left w-64">{"Nome / Razão Social"}</th>
                <th className="border p-3 text-left w-48">CPF / CNPJ</th>
                <th className="border p-3 text-left w-36">{"Ações"}</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 6 }).map((_, rowIdx) => (
                  <tr key={`sk-row-${rowIdx}`} className="animate-pulse">
                    <td className="border p-3"><div className="h-4 bg-gray-200 rounded w-full" /></td>
                    <td className="border p-3"><div className="h-4 bg-gray-200 rounded w-full" /></td>
                    <td className="border p-3"><div className="h-4 bg-gray-200 rounded w-full" /></td>
                    <td className="border p-3"><div className="h-4 bg-gray-200 rounded w-full" /></td>
                  </tr>
                ))
              ) : (
                customers.map((c) => {
                  const isIP = c.customerType === CustomerType.IndividualPerson;

                  return (
                    <tr key={c.id} className="hover:bg-gray-50">
                      <td className="border p-3">
                        {isIP ? "Pessoa Física" : "Pessoa Jurídica"}
                      </td>
                      <td className="border p-3">
                        {isIP ? c.individualPerson?.fullName : c.legalEntity?.companyName}
                      </td>
                      <td className="border p-3">
                        {isIP ? formatCpf(c.individualPerson?.cpf ?? "") : formatCnpj(c.legalEntity?.cnpj ?? "")}
                      </td>


                      <td className="border p-3">
                        <Link
                          to={`${basePath}/clientes/${c.id}`}
                          className="flex items-center gap-1 text-primary-600 hover:underline"
                        >
                          <FaEye /> {"Ver detalhes"}
                        </Link>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        )}

        {isLoading && (
          <div className="flex flex-col items-center mt-10 gap-4">
            <div className="h-4 w-40 bg-gray-200 rounded animate-pulse" />
            <div className="flex gap-2">
              {Array.from({ length: 7 }).map((_, index) => (
                <div key={`page-skel-${index}`} className="w-10 h-10 bg-gray-200 rounded animate-pulse" />
              ))}
            </div>
          </div>
        )}

        {renderPagination()}
      </div>
    </div>
  );
};

export default AdminCustomerPage;
